import DOMPurify from 'dompurify';
import './previewVarTooltip.css';

const CHIP_INLINE_STYLE =
  'background-color:#FFF;border:1px solid #E9EBF4;padding:2px 4px;' +
  'border-radius:6px;font-weight:450;display:inline-block;';

const UNRESOLVED_TOOLTIP_TEXT =
  'Data for this variable is not available yet. ' +
  'It will be filled in when the template is used.';

/**
 * Substitutes known merge-variable values into editor HTML, then wraps any
 * remaining {{…}} tokens in a styled chip so the user can tell at a glance
 * which variables weren't available at preview time.
 *
 * @param html     Raw editor HTML (e.g. `value.content` from EmailCompositionValue).
 * @param resolved Map of variable key → substituted value.
 *                 Keys should include the delimiters, e.g. `"{{candidate.first_name}}"`.
 * @returns        Sanitized HTML ready to pass as `previewHtml` to the Editor.
 */
export function buildPreviewHtml(
  html: string,
  resolved: Record<string, string>
): string {
  if (!html) return '';

  // Strip a BE-injected `style="color:red;"` that occasionally tags
  // unresolved variables before they reach the FE. Mirrors legacy
  // `addTooltipToUnsupportedVariables` — without it, unresolved tokens
  // arrive with an inline red color that overrides our chip styling.
  // Anchored on whole `style="color:red"` (whitespace + optional
  // semicolon) so a multi-property style like
  // `style="color:red; font-size:14px"` is left intact rather than
  // half-stripped to `font-size:14px"` orphan markup.
  let result = html.replace(/\sstyle=(["'])\s*color\s*:\s*red\s*;?\s*\1/gi, '');

  // When the user applies bold/italic/other formatting to text that overlaps
  // a variable token, tiptap splits the token across HTML tags, e.g.:
  //   {{interview.t</strong>ime}}  or  {{interview.<em>time</em>}}
  // Strip any HTML tags found between {{ and }} so the token is whole again
  // before we attempt string substitution.
  result = result.replace(/\{\{(?:[^}]|<[^>]+>)*\}\}/g, (match) =>
    match.replace(/<[^>]+>/g, '')
  );

  for (const [key, value] of Object.entries(resolved)) {
    if (!key || value === undefined) continue;
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escaped, 'g'), value);
  }

  // Wrap any remaining {{…}} tokens in a chip with a hover tooltip explaining
  // the value will be substituted at send time. Class-driven so the popover
  // can use CSS `:hover`; the CSS lives in `previewVarTooltip.css`.
  result = result.replace(
    /\{\{[^}]+\}\}/g,
    (match) =>
      `<span class="im-preview-var-tooltip">` +
      `<span class="im-preview-var-chip" style="${CHIP_INLINE_STYLE}">${match}</span>` +
      `<span class="im-preview-var-tooltip-text">${UNRESOLVED_TOOLTIP_TEXT}</span>` +
      `</span>`
  );

  // Preserve blank lines: empty <p> tags (with optional <br>) lose visible
  // height in the preview container when the CSS resets paragraph margins.
  // Replacing with a non-breaking space ensures the empty line stays visible.
  result = result.replace(
    /<p([^>]*)>\s*(?:<br\s*\/?>)?\s*<\/p>/gi,
    '<p$1>&nbsp;</p>'
  );

  return DOMPurify.sanitize(result, { ADD_ATTR: ['target', 'rel'] });
}
